import assign from 'lodash/assign';
import forEach from 'lodash/forEach';
import isArray from 'lodash/isArray';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import isObject from 'lodash/isObject';
import reduce from 'lodash/reduce';
import toPlainObject from 'lodash/toPlainObject';
import omit from 'lodash/omit';

// Misc / Utils
import { capitalizeFirstLetter } from './stringUtils';
import { queryStringToQueryObject } from './urlUtils';
import Filter from '../models/Filter';
import { Bedrooms } from '../types/Bedrooms';
import { PropertyTypes } from '../types/PropertyTypes';

// Types
import type { bedroomsType, propertyTypesType } from '../types/Filters';

type objectType = {
  [key: string]: string | Array<string> | number | objectType;
};
export type filterLabelsType = {
  allFiltersLabel: string;
  activeRentalsLabel: string;
  offMarketLabel: string;
  bedroomsLabel: string;
  propertyTypesLabel: string;
  distanceLabel: string;
  amenitiesLabel: string;
};
export type PotentialFilterTypes = {
  activeTypes?: Array<string>;
  activatedDays?: number;
  deactivatedDays?: number;
  propertyTypes?: Array<string>;
  listingTypes?: Array<string>;
  bedrooms?: Array<string>;
  pets?: Array<string>;
  laundry?: Array<string>;
  amenities?: Array<string>;
  distanceInMiles?: number;
  price?: number;
};
/**
 * Most filter values are objects that include all possible filters options.
 * For filter values are string | number, _flatFilterConversion used used to
 * return functions to parse these flat filters
 * ex: distanceInMiles = '25' -> distanceInMiles = 25
 */
const _flatFilterConversion = {
  orderBy: () => {
    // TODO: check if val is in acceptedValues once we support different sorting
    return 'price';
  },
  activatedDays: (val: string) => Number(val),
  deactivatedDays: (val: string) => Number(val),
  distanceInMiles: (val: string) => Number(val),
  price: (val: string) => Number(val),
};

const _defaultFilterLabels = {
  allFiltersLabel: 'Filters',
  activeRentalsLabel: 'Active rentals',
  offMarketLabel: 'Off Market',
  bedroomsLabel: 'All beds',
  propertyTypesLabel: 'All home types',
  distanceLabel: 'Any distance',
  amenitiesLabel: 'All amenities',
};

const filterUtils = {
  dedupedFilterValues(filterValue: string | Array<string>): string {
    if (isArray(filterValue)) {
      return filterValue[0];
    } else {
      return filterValue;
    }
  },
  filterObjToQueryString(filterObj: objectType): string {
    if (!filterObj || isEmpty(filterObj)) {
      return '';
    }

    return (
      '?' +
      Object.entries(filterObj)
        .map((entry) => {
          if (isObject(entry[1])) {
            return (
              entry[0] +
              '=' +
              Object.keys(entry[1])
                // @ts-ignore
                .filter((key) => entry[1][key])
                .join('-')
            );
          }
          return entry[0] + '=' + entry[1];
        })
        .join('&')
    );
  },
  partialFilterToFilterParams(_filterObj: objectType) {
    /**
     * partialFilterToFilterParams returns two filter objects - one
     * comma-separated (for GraphQL) and one hyphen-separated (for
     * routing)
     */
    const filterObj = toPlainObject(_filterObj);

    const createFilterParams = (delimeter: '-' | ',') => {
      return reduce(
        filterObj,
        (accum, value, key) => {
          if (isObject(value)) {
            const truthyKeys = Object.keys(value)
              // @ts-ignore
              .filter((truthyKey) => value[truthyKey]) as Array<any>;

            // deselecting all options will return 'none' (RG-247)
            accum[key] = truthyKeys.length ? truthyKeys.join(delimeter) : 'none';
          } else {
            accum[key] = value;
          }
          return accum;
        },
        {} as objectType,
      );
    };

    const filterForGraphQL = createFilterParams(',');
    const filterForUrl = createFilterParams('-');
    // delete default, truthy activeTypes from url (graphQL always requires activeTypes)
    if (filterForUrl.activeTypes === 'active-inactive') {
      delete filterForUrl.activeTypes;
    }

    return {
      filterForGraphQL,
      filterForUrl,
    };
  },
  getFilterDiff({
    baseFilter,
    editedFilter,
    excludeFilters = [],
  }: {
    baseFilter: objectType;
    editedFilter: objectType;
    excludeFilters?: Array<string>;
  }) {
    const filterDiff = {} as objectType;
    const filterKeys = omit(baseFilter, excludeFilters);
    for (const key in filterKeys) {
      if (!isEqual(baseFilter[key], editedFilter[key])) {
        if (isObject(baseFilter[key]) && isObject(editedFilter[key])) {
          // @ts-ignore - below have been checked to be objects
          filterDiff[key] = filterUtils.getFilterDiff({
            // @ts-ignore
            baseFilter: baseFilter[key],
            // @ts-ignore
            editedFilter: editedFilter[key],
          });
        } else {
          filterDiff[key] = editedFilter[key];
        }
      }
    }
    return filterDiff;
  },
  getActiveFilters(filter: objectType): PotentialFilterTypes {
    const defaultFilter = toPlainObject(new Filter());
    const activeFilters = filterUtils.getFilterDiff({
      baseFilter: defaultFilter,
      editedFilter: filter,
    });

    Object.entries(activeFilters).forEach(([k, v]) => {
      if (isObject(v)) {
        const fullFilter = assign({}, defaultFilter[k], activeFilters[k]);
        activeFilters[k] = Object.keys(fullFilter).filter(
          (opt) => fullFilter[opt], // keep truthy options only
        );
      }
    });

    return activeFilters;
  },
  getFilterLabels(currFilter: objectType): filterLabelsType {
    const activeFilters = filterUtils.getActiveFilters(currFilter);

    /**
     * note: numFiltersSet !== Object.keys(filterDiff).length
     * ex: numFiltersSet counts `cats` and `dogs` independently
     */
    let numFiltersSet = 0;
    const filterLabels = { ..._defaultFilterLabels } as filterLabelsType;
    let amenitiesFilter = [] as Array<string>;

    if (!activeFilters.activeTypes || (activeFilters.activeTypes && activeFilters.activeTypes.includes('active'))) {
      if (activeFilters.activatedDays) {
        filterLabels.activeRentalsLabel = `Active rentals ${activeFilters.activatedDays} days`;
        numFiltersSet++;
      } else {
        filterLabels.activeRentalsLabel = 'Active rentals';
      }
    } else {
      filterLabels.activeRentalsLabel = 'Hiding active rentals';
      numFiltersSet++;
    }

    if (!activeFilters.activeTypes || (activeFilters.activeTypes && activeFilters.activeTypes.includes('inactive'))) {
      if (activeFilters.deactivatedDays && activeFilters.deactivatedDays !== 30) {
        filterLabels.offMarketLabel = `Off market ${activeFilters.deactivatedDays} days`;
        numFiltersSet++;
      } else {
        filterLabels.offMarketLabel = 'Off market';
      }
    } else {
      filterLabels.offMarketLabel = 'Hiding off market';
      numFiltersSet++;
    }

    if (activeFilters.bedrooms) {
      if (activeFilters.bedrooms[0] === '0') {
        activeFilters.bedrooms[0] = 'Studio';
      }
      filterLabels.bedroomsLabel = activeFilters.bedrooms.join(', ');

      if (filterLabels.bedroomsLabel === 'Studio') {
        filterLabels.bedroomsLabel += 's';
      } else {
        filterLabels.bedroomsLabel += ' beds';
      }
      filterLabels.bedroomsLabel = filterLabels.bedroomsLabel.split('plus').join('+');
      numFiltersSet++;
    }
    if (activeFilters.propertyTypes) {
      filterLabels.propertyTypesLabel =
        activeFilters.propertyTypes.length === 1
          ? activeFilters.propertyTypes[0] === 'townhouse'
            ? 'Townhomes'
            : capitalizeFirstLetter(activeFilters.propertyTypes[0]) + 's'
          : `${activeFilters.propertyTypes.length} home types`;
      numFiltersSet++;
    }
    if (activeFilters.distanceInMiles) {
      filterLabels.distanceLabel = `Within ${currFilter.distanceInMiles} mi`;
      numFiltersSet++;
    }
    if (activeFilters.pets) {
      amenitiesFilter = amenitiesFilter.concat(activeFilters.pets);
    }
    if (activeFilters.laundry) {
      amenitiesFilter = amenitiesFilter.concat(activeFilters.laundry);
    }
    if (activeFilters.amenities) {
      amenitiesFilter = amenitiesFilter.concat(activeFilters.amenities);
    }
    if (amenitiesFilter.length) {
      filterLabels.amenitiesLabel = `${amenitiesFilter.length} ${
        amenitiesFilter.length === 1 ? 'amenity' : 'amenities'
      }`;
    }
    numFiltersSet += amenitiesFilter.length;

    filterLabels.allFiltersLabel = numFiltersSet
      ? `${numFiltersSet} ${numFiltersSet === 1 ? 'filter' : 'filters'} set`
      : 'Filters';

    return filterLabels;
  },
  queryObjToFilterObj(query: objectType) {
    const filterObj = {} as objectType;
    const defaultFilter = toPlainObject(new Filter());
    const validParams = Object.keys(defaultFilter);

    forEach(query, (filterValue, filterKey) => {
      // look at only valid filter params
      if (validParams.includes(filterKey)) {
        // delimeter = '-' for server (req.query). delimeter = ',' for client (urlUtils)
        const delimeters = ['-', ','];
        const queryArray = filterUtils
          .dedupedFilterValues(filterValue as string | Array<string>)
          .split(new RegExp(delimeters.join('|'), 'g'));

        // do nothing if no filter value exists in url
        if (queryArray.length) {
          // @ts-ignore
          if (_flatFilterConversion[filterKey]) {
            // @ts-ignore
            filterObj[filterKey] = _flatFilterConversion[filterKey](queryArray[0]);
          } else {
            /**
             * For filters with objects as values, start with all values
             * set to false and then replace with true. This is because
             * the url will only contain truthy values.
             */
            filterObj[filterKey] = defaultFilter[filterKey];
            Object.keys(filterObj[filterKey]).forEach((key) => {
              // @ts-ignore
              filterObj[filterKey][key] = false;
            });

            forEach(queryArray, (f) => {
              // only update values that exist on the partial filter
              if (Object.keys(filterObj[filterKey]).includes(f)) {
                // @ts-ignore
                filterObj[filterKey][f] = true;
              }
            });
          }
        }
      }
    });

    return filterObj;
  },
  queryStringToGraphQLParams(string: string) {
    /**
     * GraphQL expects certain values to be numbers.
     * queryStringToGraphQLParams will take a url query string and convert it to
     * an object of type { [key: string]: string; } via queryStringToQueryObj
     * and then convert values with _flatFilterConversion
     */
    const filterParams = queryStringToQueryObject(string);
    return reduce(
      filterParams,
      (accum, val, key) => {
        // @ts-ignore
        if (_flatFilterConversion[key]) {
          // @ts-ignore
          accum[key] = _flatFilterConversion[key](val);
        } else {
          // @ts-ignore
          accum[key] = val;
        }
        return accum;
      },
      {},
    );
  },
  filterToBedroomsEnum(filter: bedroomsType): Bedrooms {
    if (filter['0'] && filter['1'] && filter['2'] && filter['3'] && filter['4plus']) {
      return Bedrooms.ALL;
    } else if (filter['0']) {
      return Bedrooms.STUDIO;
    } else if (filter['1']) {
      return Bedrooms.ONE;
    } else if (filter['2']) {
      return Bedrooms.TWO;
    } else if (filter['3']) {
      return Bedrooms.THREE;
    } else if (filter['4plus']) {
      return Bedrooms.FOURPLUS;
    }
    return Bedrooms.ALL;
  },
  bedroomsEnumToFilter(bedrooms: Bedrooms): bedroomsType {
    const emptyFilter = {
      '0': false,
      '1': false,
      '2': false,
      '3': false,
      '4plus': false,
    };
    const allBedroomsFilter = {
      '0': true,
      '1': true,
      '2': true,
      '3': true,
      '4plus': true,
    };
    switch (bedrooms) {
      case Bedrooms.ALL:
        return allBedroomsFilter;
      case Bedrooms.STUDIO:
        return { ...emptyFilter, '0': true };
      case Bedrooms.ONE:
        return { ...emptyFilter, '1': true };
      case Bedrooms.TWO:
        return { ...emptyFilter, '2': true };
      case Bedrooms.THREE:
        return { ...emptyFilter, '3': true };
      case Bedrooms.FOURPLUS:
        return { ...emptyFilter, '4plus': true };
      default:
        return allBedroomsFilter;
    }
  },
  filterToPropertyTypesEnum(filter: propertyTypesType): PropertyTypes {
    if (filter['condo'] && filter['apartment'] && filter['house'] && filter['townhouse']) {
      return PropertyTypes.ALL;
    } else if (filter['condo'] && filter['apartment']) {
      return PropertyTypes.APTSANDCONDOS;
    } else if (filter['house']) {
      return PropertyTypes.HOUSE;
    } else if (filter['townhouse']) {
      return PropertyTypes.TOWNHOUSE;
    }
    return PropertyTypes.ALL;
  },
  propertyTypesEnumToFilter(propertyTypes: PropertyTypes): propertyTypesType {
    const emptyFilter = {
      apartment: false,
      condo: false,
      house: false,
      townhouse: false,
    };
    const allTypesFilter = {
      apartment: true,
      condo: true,
      house: true,
      townhouse: true,
    };
    switch (propertyTypes) {
      case PropertyTypes.ALL:
        return allTypesFilter;
      case PropertyTypes.APTSANDCONDOS:
        return { ...emptyFilter, apartment: true, condo: true };
      case PropertyTypes.HOUSE:
        return { ...emptyFilter, house: true };
      case PropertyTypes.TOWNHOUSE:
        return { ...emptyFilter, townhouse: true };
      default:
        return allTypesFilter;
    }
  },
};

export default filterUtils;
